import React from "react";
import { Helmet } from "react-helmet-async";
import TopNavbar from "@/components/layout/TopNavbar";
import Footer from "@/components/home/Footer";
import { Button } from "@/components/ui/button";
import { OptimizedImage } from "@/components/ui";
import { Users, Star, Calendar, Heart } from "lucide-react";
import { Link } from 'react-router-dom';

const HERO_IMAGE_URL = "https://media.hivetivity.com/little-cute-baby-boy-instructor-with-kid-mother-with-son.jpg";

const SwimClasses = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Swim Class Management Software - Hivetivity</title>
        <meta name="description" content="Streamline your swim school operations with Hivetivity. Manage classes, track progress, and communicate with parents efficiently." />
      </Helmet>

      <TopNavbar />

      <main>
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-b from-cyan-50 to-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#5a5a59] mb-6">
                  Make Waves with Smart Swim School Management
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Simplify your swim school operations and focus on what matters most - teaching water safety and confidence.
                </p>
                <Link to="/business-trial">
                  <Button className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg">
                    Start Free Trial
                  </Button>
                </Link>
              </div>
              <div className="relative">
                <img
                  src={HERO_IMAGE_URL}
                  alt="Swim instructor with child and parent in pool"
                  width={600}
                  height={400}
                  className="rounded-2xl shadow-xl"
                  style={{ objectFit: 'cover', width: '100%', height: 'auto' }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-[#5a5a59] mb-12">
              Everything Your Swim School Needs
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="p-6 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors">
                <Users className="w-12 h-12 text-blue-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Class Management</h3>
                <p className="text-gray-600">Organize levels, track attendance, and manage pool schedules.</p>
              </div>
              <div className="p-6 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors">
                <Star className="w-12 h-12 text-blue-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Progress Tracking</h3>
                <p className="text-gray-600">Monitor student development and celebrate achievements.</p>
              </div>
              <div className="p-6 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors">
                <Calendar className="w-12 h-12 text-blue-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Smart Scheduling</h3>
                <p className="text-gray-600">Effortlessly manage instructors and pool time slots.</p>
              </div>
              <div className="p-6 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors">
                <Heart className="w-12 h-12 text-blue-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Safety First</h3>
                <p className="text-gray-600">Keep track of certifications and safety requirements.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-20 bg-blue-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-[#5a5a59] mb-8">
                Trusted by Swim Schools
              </h2>
              <blockquote className="text-xl text-gray-600 italic mb-6">
                "Hivetivity has made managing our swim school so much easier. The progress tracking and parent communication features are game-changers."
              </blockquote>
              <p className="font-semibold text-[#5a5a59]">
                - Lisa Chen, Aquatic Academy Director
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-[#5a5a59] mb-6">
              Ready to Dive In?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join hundreds of swim schools using Hivetivity to create safer, more efficient swimming programs.
            </p>
            <Link to="/business-trial">
              <Button className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg">
                Start Your Free Trial Today
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SwimClasses;
